import type {
  SolarShareBlockerCode,
  SolarShareFixStep,
  SolarShareNotReadyReason,
} from "~/api/dto/solar-presentation.dto"

import { LIGHTREACH_PRICING_OUT_OF_DATE } from "../solar-lightreach-copy"

export const SOLAR_NOT_READY_PROMPT = `Pricing and savings must be current before you share this proposal.`
export const SOLAR_PUBLISHED_STALE_PROMPT = `Pricing or savings is out of date, so the homeowner sees "Pricing is being updated" instead of amounts. Fix the items below to restore them.`

/** Financing blockers name no partner: Credit Human, Concert and LightReach all raise them. */
export const SOLAR_BLOCKER_COPY: Record<
  SolarShareBlockerCode,
  { readonly text: string; readonly fixLabel: string }
> = {
  NO_ACTIVE_DESIGN: {
    text: `This proposal has no system design yet.`,
    fixLabel: `Open System Design`,
  },
  PRICING_NOT_CALCULATED: {
    text: `System Pricing hasn't been calculated.`,
    fixLabel: `Open System Pricing`,
  },
  PRICING_OUT_OF_DATE: {
    text: `Pricing is out of date. Refresh Pricing so the homeowner sees current amounts.`,
    fixLabel: `Open System Pricing`,
  },
  FINANCE_PARTNER_UNAVAILABLE: {
    text: `The selected finance partner isn't offered for this proposal now. Choose another finance partner so the homeowner sees current financing.`,
    fixLabel: `Open Finance Partner`,
  },
  LOAN_PRODUCT_UNAVAILABLE: {
    text: `The selected Loan Product is no longer available. Choose another Loan Product so the homeowner sees a current payment.`,
    fixLabel: `Open Loan Product`,
  },
  QUOTE_NOT_CURRENT: {
    text: `The financing payment is out of date or unavailable. Refresh Pricing so the homeowner sees a current payment.`,
    fixLabel: `Open System Pricing`,
  },
  SAVINGS_NOT_CALCULATED: {
    text: `Savings haven't been calculated.`,
    fixLabel: `Go to savings`,
  },
  SAVINGS_OUT_OF_DATE: {
    text: `Savings are out of date. Recalculate savings so the homeowner sees current figures.`,
    fixLabel: `Go to savings`,
  },
}

const UNKNOWN_BLOCKER = {
  text: `Pricing or savings isn't ready to share yet.`,
  fixLabel: `Open the proposal`,
}

/** The pricing blockers Refresh Pricing clears, unless an offer sets the price. */
const PRICE_BLOCKERS: ReadonlySet<SolarShareBlockerCode> = new Set([
  `PRICING_NOT_CALCULATED`,
  `PRICING_OUT_OF_DATE`,
  `QUOTE_NOT_CURRENT`,
])

/**
 * A LightReach plan's price comes from choosing an offer, so the server routes
 * its pricing blockers to Loan Product, where Get LightReach pricing runs.
 */
export const SOLAR_LIGHTREACH_PRICE_BLOCKER_COPY = {
  text: `${LIGHTREACH_PRICING_OUT_OF_DATE}, then choose an offer so the homeowner sees current amounts.`,
  fixLabel: `Open Loan Product`,
} as const

/** A blocker's copy; one this client doesn't know yet still renders. */
export const solarBlockerCopy = (
  code: SolarShareBlockerCode,
  step?: SolarShareFixStep,
): { readonly text: string; readonly fixLabel: string } =>
  step === `loan-product` && PRICE_BLOCKERS.has(code)
    ? SOLAR_LIGHTREACH_PRICE_BLOCKER_COPY
    : (SOLAR_BLOCKER_COPY[code] ?? UNKNOWN_BLOCKER)

export const SOLAR_EXPORT_CHECKING_HINT = `Checking pricing and savings`

/** The readiness read failed for good, so export can't tell whether it may start. */
export const SOLAR_EXPORT_READINESS_FAILED_PROMPT = `Unable to check pricing and savings.`

/** Why Export To PDF is unavailable, from the readiness reason. */
export const SOLAR_EXPORT_NOT_READY_COPY: Record<
  SolarShareNotReadyReason,
  string
> = {
  no_active_design: `Add a system design before you export.`,
  not_calculated: `Calculate pricing and savings before you export.`,
  out_of_date: `Pricing or savings is out of date. Refresh them before you export.`,
}

/** The export lost a race to a pricing or savings change mid-render. */
export const SOLAR_EXPORT_CONFLICT_PROMPT = `Pricing changed, refresh and try again.`
